import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useUnretireAssetAssetAssetIdUnretirePost } from "kubb";
import type React from "react";
import { useState } from "react";
import type { Asset } from "utils/object";
import { ErrorBanner } from "../../../components/Error/ErrorBanner.tsx";
import CancelButton from "../../../components/Modals/CancelButton.tsx";
import CreateButton from "../../../components/Modals/CreateButton.tsx";

interface AssetUnretireModalProps {
  asset: Asset;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function AssetUnretireModal({
  asset,
  isOpen,
  onClose,
  onSuccess,
}: AssetUnretireModalProps): React.ReactElement {
  const queryClient = useQueryClient();
  const [error, setError] = useState<Error | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const unretireMutation = useUnretireAssetAssetAssetIdUnretirePost();

  const handleSubmit = async () => {
    setError(undefined);
    setIsSubmitting(true);

    try {
      await unretireMutation.mutateAsync({
        asset_id: asset.asset_id,
        params: { company_id: asset.company_id },
      });
      await queryClient.invalidateQueries({
        queryKey: [{ url: "/asset" }, { asset_id: asset.asset_id }],
      });

      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-md rounded bg-white p-6 dark:bg-gray-800">
          <DialogTitle className="text-lg font-medium mb-4">
            Unretire Asset
          </DialogTitle>

          <div className="mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to unretire asset{" "}
              <strong>{asset.serial_number}</strong>? The asset will be restored
              to active status.
            </p>
          </div>

          <ErrorBanner error={error} />

          <div className="flex gap-3 justify-end">
            <CancelButton onClick={onClose}>Cancel</CancelButton>
            <CreateButton
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              loading={isSubmitting}
              labelText={isSubmitting ? "Unretiring..." : "Unretire Asset"}
            />
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
